import jsPDF from "jspdf";

// Colors — align with brand
const INDIGO = [30, 27, 75];
const AMBER = [217, 119, 6];
const STONE_600 = [87, 83, 78];
const STONE_400 = [168, 162, 158];
const GOLD_TEXT = [252, 211, 77];

const MARGIN = 14;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;

// ───────── low-level primitives ─────────
function pageBottomGuard(doc, y, needed = 20) {
  if (y + needed > 285) {
    doc.addPage();
    return 20;
  }
  return y;
}

function section(doc, title, y) {
  y = pageBottomGuard(doc, y, 14);
  doc.setFontSize(9);
  doc.setTextColor(...AMBER);
  doc.setFont("helvetica", "bold");
  doc.text(title.toUpperCase(), MARGIN, y);
  y += 2;
  doc.setDrawColor(...AMBER);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, MARGIN + 30, y);
  return y + 6;
}

function paragraph(doc, text, y, opts = {}) {
  const size = opts.size || 10;
  const color = opts.color || STONE_600;
  const family = opts.serif ? "times" : "helvetica";
  doc.setFontSize(size);
  doc.setTextColor(...color);
  doc.setFont(family, opts.bold ? "bold" : (opts.italic ? "italic" : "normal"));
  const lines = doc.splitTextToSize(text || "", CONTENT_W);
  lines.forEach((line) => {
    y = pageBottomGuard(doc, y, size * 0.6);
    doc.text(line, MARGIN, y);
    y += size * 0.5;
  });
  return y + 2;
}

function bulletList(doc, items, y, opts = {}) {
  const size = opts.size || 10;
  const color = opts.color || STONE_600;
  items.forEach((it) => {
    y = pageBottomGuard(doc, y, size * 0.9);
    doc.setFontSize(size);
    doc.setTextColor(...AMBER);
    doc.setFont("helvetica", "bold");
    doc.text(opts.marker || "•", MARGIN, y);
    doc.setTextColor(...color);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(it, CONTENT_W - 5);
    lines.forEach((line, i) => {
      if (i > 0) {
        y += size * 0.55;
        y = pageBottomGuard(doc, y, size * 0.7);
      }
      doc.text(line, MARGIN + 5, y);
    });
    y += size * 0.65;
  });
  return y + 2;
}

function scoreBar(doc, label, score, y) {
  doc.setFontSize(9);
  doc.setTextColor(...STONE_600);
  doc.setFont("helvetica", "normal");
  doc.text(label, MARGIN, y);
  doc.setTextColor(...INDIGO);
  doc.setFont("helvetica", "bold");
  doc.text(`${score}%`, MARGIN + CONTENT_W - 12, y);
  y += 1.5;
  doc.setFillColor(240, 238, 234);
  doc.roundedRect(MARGIN, y, CONTENT_W, 2.2, 1, 1, "F");
  doc.setFillColor(...AMBER);
  doc.roundedRect(MARGIN, y, (CONTENT_W * score) / 100, 2.2, 1, 1, "F");
  return y + 8;
}

function pageTitle(doc, title, y) {
  doc.setFontSize(20);
  doc.setTextColor(...INDIGO);
  doc.setFont("times", "bold");
  doc.text(title, MARGIN, y);
  return y + (title === "The Numbers" ? 9 : 11);
}

// ───────── page renderers ─────────
function renderCover(doc, report, kundali) {
  doc.setFillColor(...INDIGO);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");

  // Gold hairlines flanking a small dot ornament
  doc.setDrawColor(...AMBER);
  doc.setLineWidth(0.25);
  doc.line(PAGE_W / 2 - 30, 78, PAGE_W / 2 - 8, 78);
  doc.line(PAGE_W / 2 + 8, 78, PAGE_W / 2 + 30, 78);
  doc.setFillColor(...AMBER);
  doc.circle(PAGE_W / 2, 78, 0.6, "F");

  // Text wordmark: "Anubandh" (white serif) + ".ai" (gold serif)
  doc.setFontSize(34);
  doc.setFont("times", "normal");
  doc.setTextColor(255, 255, 255);
  const anubandh = "Anubandh";
  const ai = ".ai";
  const aW = doc.getTextWidth(anubandh);
  const iW = doc.getTextWidth(ai);
  const startX = (PAGE_W - (aW + iW)) / 2;
  doc.text(anubandh, startX, 92);
  doc.setTextColor(...GOLD_TEXT);
  doc.text(ai, startX + aW, 92);

  // Tagline + second hairline
  doc.setFontSize(8);
  doc.setTextColor(...GOLD_TEXT);
  doc.setFont("helvetica", "normal");
  doc.text("MARRIAGE INTELLIGENCE PLATFORM", PAGE_W / 2, 100, { align: "center" });
  doc.setDrawColor(...AMBER);
  doc.setLineWidth(0.25);
  doc.line(PAGE_W / 2 - 20, 108, PAGE_W / 2 + 20, 108);

  // Report label + name
  doc.setFontSize(9);
  doc.text("MARRIAGE INTELLIGENCE REPORT", PAGE_W / 2, 138, { align: "center" });
  doc.setFontSize(30);
  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "bold");
  doc.text(report.name || "Match Report", PAGE_W / 2, 160, { align: "center" });

  // Sub-meta
  doc.setFontSize(11);
  doc.setTextColor(180, 178, 190);
  doc.setFont("helvetica", "normal");
  const meta = [report.profile?.profession, report.profile?.city].filter(Boolean).join("  ·  ");
  if (meta) doc.text(meta, PAGE_W / 2, 170, { align: "center" });

  // Fusion score badge
  const fusion = kundali?.fusion_percentage || report.overall_score;
  doc.setFillColor(...AMBER);
  doc.roundedRect(PAGE_W / 2 - 30, 200, 60, 30, 4, 4, "F");
  doc.setFontSize(9);
  doc.setTextColor(...INDIGO);
  doc.text("KUNDALI × AI FUSION SCORE", PAGE_W / 2, 209, { align: "center" });
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text(`${fusion}%`, PAGE_W / 2, 222, { align: "center" });

  // Date + confidentiality
  doc.setFontSize(9);
  doc.setTextColor(180, 178, 190);
  doc.setFont("helvetica", "normal");
  const dateOpts = { day: "numeric", month: "long", year: "numeric" };
  const genDate = report.generated_at
    ? new Date(report.generated_at).toLocaleDateString("en-IN", dateOpts)
    : new Date().toLocaleDateString("en-IN", dateOpts);
  doc.text(`Generated on ${genDate}`, PAGE_W / 2, 260, { align: "center" });
  doc.setFontSize(8);
  doc.text("For family review · Confidential", PAGE_W / 2, 275, { align: "center" });
}

function renderScoresPage(doc, report, kundali) {
  doc.addPage();
  let y = pageTitle(doc, "The Numbers", 20);

  y = section(doc, "Overall Alignment", y);
  y = scoreBar(doc, "AI Compatibility", report.overall_score, y);
  if (kundali) {
    y = scoreBar(doc, `Vedic Kundali (${kundali.vedic?.total || 0}/36 gunas)`, kundali.vedic?.percentage || 0, y);
    y = scoreBar(doc, "Kundali × AI Fusion", kundali.fusion_percentage, y);
  }
  if (typeof report.confidence === "number") {
    y = scoreBar(doc, "Score Confidence", report.confidence, y);
  }

  // Dimension Radar — with Sr. No. prefix (1. Personality  83%)
  y += 4;
  y = section(doc, "Dimension Radar", y);
  Object.entries(report.dimension_scores || {}).forEach(([dim, sc], i) => {
    y = scoreBar(doc, `${i + 1}. ${dim}`, sc, y);
  });

  const chem = report.ai?.chemistry_scores;
  if (chem) {
    y += 4;
    y = section(doc, "Two of You — Chemistry", y);
    y = scoreBar(doc, "Intellectual Chemistry", chem.intellectual || 0, y);
    y = scoreBar(doc, "Emotional Resonance", chem.emotional || 0, y);
    y = scoreBar(doc, "Life-Rhythm Fit", chem.life_rhythm || 0, y);
    y = scoreBar(doc, "Values Alignment", chem.values || 0, y);
  }
}

function renderNarrativePage(doc, report) {
  doc.addPage();
  let y = pageTitle(doc, "AI Intelligence", 20);

  y = paragraph(doc, report.ai?.headline || "", y, { size: 13, color: INDIGO, bold: true, serif: true });
  y += 2;

  y = section(doc, "Shared Strengths", y);
  y = bulletList(doc, report.ai?.shared_strengths || [], y, { marker: "◆" });

  y = section(doc, "Potential Challenges", y);
  y = bulletList(doc, report.ai?.potential_challenges || [], y, { marker: "◇" });

  if ((report.ai?.deal_breakers || []).length) {
    y = section(doc, "Deal-Breaker Alerts", y);
    y = bulletList(doc, report.ai.deal_breakers, y, { marker: "!" });
  }

  y = section(doc, "Recommended Conversations", y);
  y = bulletList(doc, report.ai?.recommended_conversations || [], y, { marker: "→" });

  y = section(doc, "Family Fit", y);
  y = paragraph(doc, report.ai?.family_fit || "", y);

  const gt = report.ai?.growth_trajectory;
  if (gt) {
    y = section(doc, "Growth Trajectory", y);
    y = paragraph(doc, `${gt.verdict || ""} — Trajectory score ${gt.score || 0}%.`, y);
    if (gt.drivers?.length) y = bulletList(doc, gt.drivers, y, { marker: "↗" });
  }

  y = section(doc, "Readiness Actions", y);
  y = bulletList(doc, report.ai?.readiness_actions || [], y, { marker: "✦" });
}

function renderFirstQuestions(doc, questions, y) {
  y = section(doc, "First Three Questions", y);
  doc.setFontSize(9);
  doc.setTextColor(...STONE_400);
  doc.setFont("helvetica", "italic");
  doc.text("Specific to this pair — designed for your first meaningful conversation.", MARGIN, y);
  y += 6;
  questions.forEach((q, i) => {
    y = pageBottomGuard(doc, y, 14);
    doc.setFontSize(16);
    doc.setTextColor(...AMBER);
    doc.setFont("helvetica", "bold");
    doc.text(`${i + 1}.`, MARGIN, y);
    y = paragraph(doc, q, y - 2, { size: 11, color: INDIGO }) + 3;
  });
  return y;
}

function renderElderLetter(doc, letter, y) {
  y += 4;
  y = pageBottomGuard(doc, y, 30);
  const lines = doc.splitTextToSize(letter, CONTENT_W - 10);
  const boxHeight = lines.length * 5 + 18;
  const boxY = y;
  // Bordered box in warm cream
  doc.setFillColor(250, 245, 232);
  doc.setDrawColor(...AMBER);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, boxY, CONTENT_W, boxHeight, 3, 3, "FD");
  // Label
  doc.setFontSize(9);
  doc.setTextColor(...AMBER);
  doc.setFont("helvetica", "bold");
  doc.text("A NOTE FOR THE ELDERS", MARGIN + 5, boxY + 7);
  // Body (serif italic)
  doc.setFontSize(11);
  doc.setTextColor(...INDIGO);
  doc.setFont("times", "italic");
  let ly = boxY + 14;
  lines.forEach((line) => {
    doc.text(line, MARGIN + 5, ly);
    ly += 5;
  });
  return boxY + boxHeight + 6;
}

function renderFamilyPage(doc, report) {
  const questions = report.ai?.first_conversation || [];
  const letter = report.ai?.elder_letter;
  if (!questions.length && !letter) return;
  doc.addPage();
  let y = pageTitle(doc, "For Family Conversation", 20);
  if (questions.length) y = renderFirstQuestions(doc, questions, y);
  if (letter) y = renderElderLetter(doc, letter, y);
}

function renderFooters(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    if (i === 1) continue; // skip cover
    // Branded wordmark on left: "Anubandh" (serif stone) + ".ai" (gold)
    doc.setFontSize(9);
    doc.setFont("times", "normal");
    doc.setTextColor(...STONE_600);
    doc.text("Anubandh", MARGIN, 292);
    const wm = doc.getTextWidth("Anubandh");
    doc.setTextColor(...AMBER);
    doc.text(".ai", MARGIN + wm, 292);
    // Tagline suffix + page count
    doc.setTextColor(...STONE_400);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(" · Marriage Intelligence Report · Confidential", MARGIN + wm + doc.getTextWidth(".ai"), 292);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W - MARGIN, 292, { align: "right" });
  }
}

// ───────── public entry ─────────
export function generateReportPDF(report, kundali) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  renderCover(doc, report, kundali);
  renderScoresPage(doc, report, kundali);
  renderNarrativePage(doc, report);
  renderFamilyPage(doc, report);
  renderFooters(doc);
  const safeName = (report.name || "Match").replace(/[^\w]+/g, "_");
  doc.save(`Anubandh_MIR_${safeName}.pdf`);
}
